import { useCallback, useMemo, useState } from "react";
import {
  Login,
  RegistrationForm,
  TokenType,
  UserMapToModelType,
} from "../models/types/userTypes";
import { useUser } from "../providers/UserProvider";
import useAxiosinterceptors from "../../hooks/useAxiosinterceptors";
import {
  editUser,
  getUserFromServer,
  login,
  signup,
} from "../services/userApi";
import {
  getUser,
  removeToken,
  setTokenInLocalStorage,
} from "../services/localStorage";
import { useNavigate } from "react-router-dom";
import ROUTES from "../../routes/routesModel";

import normalizeUser from "../helpers/normalization/normalizeUser";
import { useSnack } from "../../provider/SnackbarProvider";
import normalizeEditUser from "../helpers/normalization/normalizeEditUser";

type errorType = null | string;
const useHandleUser = () => {
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<errorType>(null);

  const { setUser, setToken, user } = useUser();
  const snack = useSnack();
  useAxiosinterceptors();
  const navigate = useNavigate();
  const requestStatus = useCallback(
    (loading: boolean, errorMessage: errorType, userData: null | TokenType) => {
      setLoading(loading);
      setError(errorMessage);
      setUser(userData);
    },
    [setUser]
  );

  const handleLogin = useCallback(
    async (user: Login) => {
      try {
        setLoading(true);
        const token = await login(user);
        setTokenInLocalStorage(token);
        setToken(token);
        const userFromLocalstorage = getUser();
        requestStatus(false, null, userFromLocalstorage);
        localStorage.removeItem("favorites");
        navigate(ROUTES.ROOT);
      } catch (error) {
        if (typeof error === "string") requestStatus(false, error, null);
      }
    },
    [navigate, requestStatus, setToken]
  );
  const handleLogout = useCallback(() => {
    removeToken();
    setUser(null);
  }, [setUser]);

  const handleSignup = useCallback(
    async (user: RegistrationForm) => {
      try {
        const normalizedUser = normalizeUser(user);
        await signup(normalizedUser);
        await handleLogin({
          email: user.email,
          password: user.password,
        });
        snack("success", "You have successfully registered and logged in.");
      } catch (error) {
        if (typeof error === "string") requestStatus(false, error, null);
      }
    },
    [handleLogin, requestStatus, snack]
  );

  const handleGetUser = useCallback(
    async (cardId: string) => {
      try {
        setLoading(true);
        const userFromServer = await getUserFromServer(cardId);
        requestStatus(false, null, userFromServer);
        return userFromServer;
      } catch (error) {
        if (typeof error === "string") requestStatus(false, error, null);
      }
    },
    [requestStatus]
  );

  const handleUpdateUser = useCallback(
    async (userFromClient: UserMapToModelType) => {
      try {
        setLoading(true);
        const newnormalizeUser = normalizeEditUser(userFromClient);
        const userFromServer = await editUser(newnormalizeUser);
        requestStatus(false, null, userFromServer);
        snack("success", "The business card has been successfully updated");
        navigate(ROUTES.ROOT);
      } catch (error) {
        if (typeof error === "string") return requestStatus(false, error, null);
      }
    },
    [navigate, requestStatus, snack]
  );

  const value = useMemo(() => {
    return { isLoading, error, user };
  }, [error, isLoading, user]);

  return {
    value,
    handleLogin,
    handleLogout,
    handleSignup,
    handleUpdateUser,
    handleGetUser,
  };
};

export default useHandleUser;
